import React, { useState, useContext, useEffect } from 'react';

import { Container } from '@mui/system';
import { Card, CardHeader, IconButton, Modal, Box, Typography, Stack, FormHelperText, TextField, InputAdornment, List, Button, CardContent, Divider, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add, Delete, Edit } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from "yup";
import { collection, query, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { format, parse } from 'date-fns';

import { db } from '../../firebase';
import AdminNavbar from '../../components/AdminNavbar';
import AlertsContext from '../../AlertsContext';
import CheckboxGroup from '../../components/CheckboxGroup';
import { currencyFormatter } from '../../helpers';
import DeleteDialog from '../../components/DeleteDialog';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: "80%",
  overflow: "scroll",
  scroll: "auto",
};

const schema = yup.object({
  title: yup.string().required().label('Title'),
  price: yup.number().required().label('Price'),
}).required();

const ticketCollection = collection(db, 'tickets');

export default function Tickets() {
  const { setAlert } = useContext(AlertsContext);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [currentTicket, setCurrentTicket] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [locations, setLocations] = useState([]);
  const { control, handleSubmit, reset, setValue, formState: { errors, isDirty, isValid } } = useForm({
    defaultValues: {
      title: '',
      price: 0,
      location: '',
      disabledDates: [],
      enabledDates: [],
      enabledDays: [],
    },
    resolver: yupResolver(schema)
  });
  const { fields: disabledDateFields, append: disabledDateAppend, remove: disabledDateRemove } = useFieldArray({
    control,
    name: "disabledDates",
  });
  const { fields: enabledDateFields, append: enabledDateAppend, remove: enabledDateRemove } = useFieldArray({
    control,
    name: "enabledDates",
  });
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

  const handleAddDisabledDate = () => {
    disabledDateAppend({
      from: new Date(),
      to: new Date(),
    })
  };

  const handleAddEnabledDate = () => {
    enabledDateAppend({
      from: new Date(),
      to: new Date(),
    })
  };

  const handleRemoveDisabledDate = (index) => {
    disabledDateRemove(index);
  }

  const handleRemoveEnabledDate = (index) => {
    enabledDateRemove(index);
  }

  const parseDateRange = (dates) => {
    return dates?.map(({ from, to }) => {
      return {
        from: format(new Date(from), 'yyyy-MM-dd'),
        to: format(new Date(to), 'yyyy-MM-dd'),
      }
    }) || []
  }

  const onSubmit = async data => {
    try {
      const newData = {
        ...data,
        disabledDates: parseDateRange(data?.disabledDates),
        enabledDates: parseDateRange(data?.enabledDates),
      }

      setIsLoading(true);
      if (currentTicket) {
        const ticketRef = doc(db, "tickets", currentTicket.id);
        await updateDoc(ticketRef, {
          ...newData,
        })
        setAlert("Ticket is updated successful.");
      } else {
        await addDoc(ticketCollection, {
          ...newData,
        })
        setAlert("Ticket is added successful.");
      }
      setOpenModal(false);
      setCurrentTicket();
      reset();
    } catch (e) {
      console.log(e);
      setAlert({ type: "error", message: "Error." });
    } finally {
      setIsLoading(false);
    }
  }


  const parseTicketDateString = (date) => {
    return parse(date, 'yyyy-MM-dd', new Date())
  }

  const parseTicketDateRangeString = (from, to) => {
    return {
      from: parseTicketDateString(from),
      to: parseTicketDateString(to),
    }
  }

  const editTicket = (ticket) => {
    setOpenModal(true);
    setValue("title", ticket.title);
    setValue("price", ticket.price);
    setValue("location", ticket.location);
    setValue("disabledDates", ticket.disabledDates?.map(({ from, to }) => parseTicketDateRangeString(from, to)) || []);
    setValue("enabledDates", ticket.enabledDates?.map(({ from, to }) => parseTicketDateRangeString(from, to)) || []);
    setValue('enabledDays', ticket.enabledDays || []);
    setCurrentTicket(ticket);
  }

  const deleteTicket = async (id) => {
    const ticketRef = doc(db, 'tickets', id);
    try {
      setIsLoading(true)
      await deleteDoc(ticketRef);
      setAlert("Ticket is deleted successful.");
      setOpenDialog(false);
    } catch (e) {
      setAlert({
        type: 'error',
        message: "Error."
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleCloseModal = () => {
    setCurrentTicket();
    setOpenModal(false);
    reset();
  }

  useEffect(() => {

    onSnapshot(query(collection(db, 'locations')), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });
      setLocations(list);
    });

    onSnapshot(query(ticketCollection), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });
      setTickets(list);
    });
  }, []);

  return <>
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs} >
        <AdminNavbar />
        <Container component="main" maxWidth="xs" sx={{
          marginTop: '16px',
          marginBottom: '16px',
        }}>
          <Card>
            <CardHeader action={
              <IconButton aria-label="settings" onClick={() => setOpenModal(true)}>
                <Add />
              </IconButton>
            }
              title="Tickets"
            />
            <CardContent>
              <List sx={{ width: '100%', margin: '0 auto', marginTop: 0, alignItems: 'center' }}>
                {
                  tickets && tickets.length > 0 ? (
                    <>
                      {tickets.map((ticket, index) => {
                        return (
                          <Card key={ticket.id} sx={{
                            marginTop: index === 0 ? 0 : 4,
                          }}>
                            <CardContent>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant='h6' color="text.secondary">
                                    {ticket?.title}
                                  </Typography>
                                  <Typography variant='h8' color="text.secondary">
                                    Location: {ticket?.location}
                                  </Typography>
                                  <Typography color="text.secondary">
                                    {currencyFormatter(Number(ticket?.price))}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Stack direction="row" spacing={1}>
                                    <IconButton onClick={() => editTicket(ticket)} sx={{
                                      height: '100%'
                                    }} variant="outlined" color="primary"><Edit /></IconButton>
                                    <IconButton onClick={() => {
                                      setOpenDialog(true);
                                      setCurrentTicket(ticket.id);
                                    }} sx={{
                                      height: '100%'
                                    }} variant="outlined" color="error"><Delete /></IconButton>
                                  </Stack>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </>
                  ) : <Box>
                    <Typography>No tickets.</Typography>
                  </Box>
                }
              </List>
            </CardContent>
          </Card>
        </Container>
        <Modal
          open={openModal}
          onClose={() => handleCloseModal()}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{
              mb: 4,
            }}>
              {!currentTicket ? 'Add' : 'Edit'} Ticket
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => <TextField type="text" id="outlined-basic" label="Title" variant="outlined" {...field} />}
                />
                {
                  errors?.title && (
                    <FormHelperText id="my-helper-text" error>{errors.title?.message}</FormHelperText>
                  )
                }
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => <TextField select 
                    onChange={id => field.onChange(id)}
                    label="Location"
                    {...field}
                  >
                    {
                      locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)
                    }
                </TextField>}
                />
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => <TextField type="text" label="Price" variant="outlined" InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }} {...field} />}
                />
                {
                  errors?.price && (
                    <FormHelperText error>{errors.price?.message}</FormHelperText>
                  )
                }
                <Divider textAlign="left">Disabled Dates</Divider>
                {
                  disabledDateFields.map((field, index) => (
                    <Box key={field.id}>
                      <Stack spacing={2}>
                        <Typography>Disabled Date</Typography>
                        <Controller
                          name={`disabledDates.${index}.from`}
                          control={control}
                          render={({ field }) => <DesktopDatePicker
                            label="From Date"
                            disablePast
                            inputFormat="DD/MM/YYYY"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            renderInput={(params) => <TextField {...field} {...params} />}
                          />}
                        />
                        {
                          errors?.disabledDates?.[index]?.from && (
                            <FormHelperText error>{errors?.disabledDates?.[index]?.from?.message}</FormHelperText>
                          )
                        }
                        <Controller
                          name={`disabledDates.${index}.to`}
                          control={control}
                          render={({ field }) => <DesktopDatePicker
                            label="To Date"
                            disablePast
                            inputFormat="DD/MM/YYYY"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            renderInput={(params) => <TextField {...field} {...params} />}
                          />}
                        />
                        {
                          errors?.disabledDates?.[index]?.to && (
                            <FormHelperText error>{errors?.disabledDates?.[index]?.to?.message}</FormHelperText>
                          )
                        }
                        <Button variant="text" onClick={() => handleRemoveDisabledDate(index)}>Remove</Button>
                      </Stack>
                    </Box>
                  ))
                }
                <Button variant="text" onClick={handleAddDisabledDate}>Add disabled date</Button>
                <Divider textAlign="left">Enabled Dates</Divider>
                {
                  enabledDateFields.map((field, index) => (
                    <Box key={field.id}>
                      <Stack spacing={2}>
                        <Typography>Enabled Date</Typography>
                        <Controller
                          name={`enabledDates.${index}.from`}
                          control={control}
                          render={({ field }) => <DesktopDatePicker
                            label="From Date"
                            disablePast
                            inputFormat="DD/MM/YYYY"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            renderInput={(params) => <TextField {...field} {...params} />}
                          />}
                        />
                        {
                          errors?.enabledDates?.[index]?.from && (
                            <FormHelperText error>{errors?.enabledDates?.[index]?.from?.message}</FormHelperText>
                          )
                        }
                        <Controller
                          name={`enabledDates.${index}.to`}
                          control={control}
                          render={({ field }) => <DesktopDatePicker
                            label="To Date"
                            disablePast
                            inputFormat="DD/MM/YYYY"
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            renderInput={(params) => <TextField {...field} {...params} />}
                          />}
                        />
                        {
                          errors?.enabledDates?.[index]?.to && (
                            <FormHelperText error>{errors?.enabledDates?.[index]?.to?.message}</FormHelperText>
                          )
                        }
                        <Button variant="text" onClick={() => handleRemoveEnabledDate(index)}>Remove</Button>
                      </Stack>
                    </Box>
                  ))
                }
                <Button variant="text" onClick={handleAddEnabledDate}>Add enabled date</Button>
                <Divider textAlign="left">Enabled Days</Divider>
                <CheckboxGroup
                  name="enabledDays"
                  control={control}
                  options={days.map(day => ({
                    label: day, value: day
                  }))}
                  config={{ label: "label", value: "value" }}
                  row={true}
                />
              </Stack>
              <LoadingButton
                disabled={!isDirty && !isValid}
                loading={isLoading}
                sx={{ margin: '0 auto', marginTop: 2, alignItems: 'center' }}
                type='submit'
                variant='contained'
              >
                {!currentTicket ? 'Add' : 'Update'}
              </LoadingButton>
            </form>
          </Box>
        </Modal>
        <DeleteDialog onClose={() => {
          setOpenDialog(false);
          setCurrentTicket(undefined);
        }}
          open={openDialog}
          onOk={() => deleteTicket(currentTicket)}
        />
      </LocalizationProvider>
    </>
  </>
}
